package com.nonopn.skydome.common.app.pages.estimate

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.LocalContentColor
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.view.map.MapOverlayCard
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.estimate_existing_probes_count
import com.nonopn.skywatcher.common.app.res.estimate_next_tier_hint
import com.nonopn.skywatcher.common.app.res.estimate_validate_no_probes
import com.nonopn.skywatcher.common.app.res.estimate_validate_payment
import com.nonopn.skywatcher.common.app.res.estimate_volume_discounts
import com.nonopn.skywatcher.common.preview.HotPreviewApp
import com.nonopn.skywatcher.design.local.LocalConfirmPopup
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized
import eu.codlab.compose.widgets.TextNormal
import org.jetbrains.compose.resources.stringResource

@Suppress("LongMethod")
@Composable
fun EstimateViewPayButton(
    modifier: Modifier = Modifier,
    model: EstimateScreenModel,
) {
    val uriHandler = LocalUriHandler.current
    val popup = LocalConfirmPopup.current
    val state by model.states.collectAsState()
    val estimateComputation = remember { EstimatePriceComputation() }

    val existingCount = state.dashboardProbes.size
    val newCount = state.probes.size
    val computed = estimateComputation.compute(existingCount, newCount)

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp),
        horizontalAlignment = Alignment.End
    ) {
        // Tier info card — always visible when probes placed
        if (newCount > 0) {
            DiscountTiersCard(
                estimateComputation = estimateComputation,
                computed = computed,
            )
        }

        // Pay button card
        MapOverlayCard(
            modifier = Modifier,
            columnModifier = Modifier.wrapContentWidth()
                .height(IntrinsicSize.Min),
            onClick = {
                model.checkoutEstimate(popup, uriHandler)
            }
        ) {
            if (newCount == 0) {
                Column(
                    modifier = Modifier.padding(16.dp),
                ) {
                    TextNormal(
                        modifier = Modifier,
                        text = Res.string.estimate_validate_no_probes.localized(),
                    )
                }
                return@MapOverlayCard
            }
            Column(
                modifier = Modifier.padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Row(
                    modifier = Modifier,
                    horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.End),
                    verticalAlignment = Alignment.Bottom
                ) {
                    if (computed.discount > 0) {
                        Text(
                            text = "${computed.regular}€",
                            fontSize = LocalFontSizes.current.estimates.discount,
                            fontStyle = FontStyle.Italic,
                            fontWeight = FontWeight.Thin,
                            textDecoration = TextDecoration.LineThrough
                        )

                        Row(
                            modifier = Modifier,
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.Bottom,
                        ) {
                            Text(
                                text = "${computed.discount}%",
                                fontSize = LocalFontSizes.current.estimates.discount,
                                fontWeight = FontWeight.Bold,
                            )
                        }
                    }

                    TextNormal(
                        text = "${computed.price}€",
                        fontSize = LocalFontSizes.current.estimates.price,
                        fontWeight = FontWeight.Bold,
                    )
                }

                TextNormal(
                    modifier = Modifier,
                    text = Res.string.estimate_validate_payment.localized(),
                    fontSize = LocalFontSizes.current.estimates.price,
                    fontWeight = FontWeight.Bold,
                )
            }
        }
    }
}

@Composable
private fun DiscountTiersCard(
    estimateComputation: EstimatePriceComputation,
    computed: EstimatePriceEstimation,
) {
    MapOverlayCard(
        modifier = Modifier,
        columnModifier = Modifier.wrapContentWidth()
            .height(IntrinsicSize.Min),
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            // Existing probes count
            if (computed.existingCount > 0) {
                TextNormal(
                    text = stringResource(
                        Res.string.estimate_existing_probes_count,
                        computed.existingCount
                    ),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
            }

            // Volume discounts label
            TextNormal(
                text = Res.string.estimate_volume_discounts.localized(),
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
            )

            // Tier rows
            estimateComputation.tiers.forEach { (threshold, percent) ->
                val isReached = threshold <= computed.totalCount
                val isCurrent = computed.discount == percent

                Row(
                    modifier = Modifier,
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        modifier = if (isCurrent) {
                            Modifier
                                .background(
                                    AppColor.StrikePrimary.copy(alpha = 0.15f),
                                    RoundedCornerShape(4.dp)
                                )
                                .padding(horizontal = 6.dp, vertical = 2.dp)
                        } else {
                            Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                        },
                        text = "$threshold+ → -$percent%",
                        fontSize = 12.sp,
                        fontWeight = if (isCurrent) {
                            FontWeight.Bold
                        } else {
                            FontWeight.Normal
                        },
                        color = if (isReached) {
                            AppColor.StrikePrimary
                        } else {
                            LocalContentColor.current.copy(alpha = 0.4f)
                        },
                    )

                    if (isCurrent) {
                        Text(
                            text = "✓",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Bold,
                            color = AppColor.StrikePrimary,
                        )
                    }
                }
            }

            // Next tier hint
            val nextThreshold = computed.nextTierThreshold
            val nextDiscount = computed.nextTierDiscount
            if (nextThreshold != null && nextDiscount != null) {
                val needed = nextThreshold - computed.totalCount
                TextNormal(
                    text = stringResource(
                        Res.string.estimate_next_tier_hint,
                        needed,
                        nextDiscount
                    ),
                    fontSize = 11.sp,
                    fontStyle = FontStyle.Italic,
                    color = AppColor.StrikePrimary,
                )
            }
        }
    }
}

@Preview(widthDp = 200, heightDp = 300)
@Composable
private fun EstimateViewPayButtonEmptyPreview() {
    HotPreviewApp(isDarkTheme = isSystemInDarkTheme()) {
        EstimateViewPayButton(
            modifier = Modifier.fillMaxSize(),
            model = FakeEstimateScreenModel(
                probes = listOf()
            )
        )
    }
}

@Preview(widthDp = 200, heightDp = 300)
@Composable
private fun EstimateViewPayButtonPreview() {
    HotPreviewApp(isDarkTheme = isSystemInDarkTheme()) {
        EstimateViewPayButton(
            modifier = Modifier.fillMaxSize(),
            model = FakeEstimateScreenModel(
                probes = listOf(
                    Probe("", "", 0.0, 0.0, 0, false, false, false, false),
                    Probe("", "", 0.0, 0.0, 0, false, false, false, false),
                    Probe("", "", 0.0, 0.0, 0, false, false, false, false),
                )
            )
        )
    }
}
